import React, { useState, useEffect, useRef } from 'react';
import { drawRoom, getWallCoordinates, highlightWall, drawKitchenItem } from '@/utils/roomDrawingFunctions';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Trash2, ChevronRight, Plus, RotateCw, Settings } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { applianceData } from '@/utils/appliance-data';
import WallElementEditor from './WallElementEditor';
import { convertToInches, formatValue } from '@/utils/utils';
import { useToast } from '@/hooks/use-toast';
import { useMediaQuery } from '@/hooks/use-media-query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import ApplianceSelector from '@/Pages/appliances';

const BASE_CABINET_DEPTH = 24; // inches
const BASE_CABINET_HEIGHT = 36; //inches
// const WALL_CABINET_DEPTH = 12; // inches
const MIN_CANVAS_WIDTH = 320; // Minimum width in pixels, adjust as needed
// const MIN_ISLAND_DISTANCE = 42; // Minimum distance from island to other items (in inches)

const applianceIcons = Object.fromEntries(applianceData.map(({ id, icon }) => [id, icon]));

const KitchenLayoutEditor = ({ onSave, initialKitchenLayout, floorPlanData, initialAppliancesData, onAppliancesUpdate, projectId }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [canvasSize, setCanvasSize] = useState({ width: 400, height: 300 });
    const [selectedWall, setSelectedWall] = useState(null);
    const [wallCoordinates, setWallCoordinates] = useState(floorPlanData?.wallCoordinates || {});
    const [kitchenItems, setKitchenItems] = useState(initialKitchenLayout || []);
    const [selectedItems, setSelectedItems] = useState([]);
    const [currentSelectedIndex, setCurrentSelectedIndex] = useState(0);
    const [scale, setScale] = useState(1);
    const [startX, setStartX] = useState(1);
    const [startY, setStartY] = useState(1);
    const [draggedHandle, setDraggedHandle] = useState(null);
    const [isIslandDialogOpen, setIsIslandDialogOpen] = useState(false);
    const [isDraggingItem, setisDraggingItem] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    const [appliances, setAppliances] = useState([]);
    const [wallElements, setWallElements] = useState(floorPlanData?.wallElements || {});
    const [walls, setWalls] = useState(floorPlanData?.walls || {});
    const [inputValues, setInputValues] = useState({});
    const [unit, setUnit] = useState(floorPlanData?.unit || 'in');
    const [isAddingBaseCabinet, setIsAddingBaseCabinet] = useState(false);
    const { toast } = useToast();
    const [showTip, setShowTip] = useState(() => {
        // Check if there are any existing elements
        const hasExistingElements = 
            (initialKitchenLayout && initialKitchenLayout.length > 0) || // Check for kitchen items
            (floorPlanData?.wallElements && Object.values(floorPlanData.wallElements).some(elements => elements.length > 0)); // Check for wall elements
        
        return !hasExistingElements; // Only show tips if there are no existing elements
    });
    const isDesktop = useMediaQuery('(min-width: 768px)');
    const [editorHeight, setEditorHeight] = useState(0);
    const [editorWidth, setEditorwidth] = useState(0);
    const [addedAppliances, setAddedAppliances] = useState(new Set());
    const [isApplianceMenuOpen, setIsApplianceMenuOpen] = useState(false);
    const [isApplianceSelectorOpen, setIsApplianceSelectorOpen] = useState(false);
    const applianceMenuRef = useRef(null);

    useEffect(() => {
        const updateCanvasSize = () => {
            if (window) {
                const availableWidth = isDesktop ? window.innerWidth * 0.6 : window.innerWidth;
                // const containerHeight = window.innerHeight * 0.6;
                const availableHeight = isDesktop
                    ? window.innerHeight - 260 // Use full container height for desktop minus footer toolbar and header
                    : window.innerHeight - 300;
                const aspectRatio = 4 / 3;

                let newWidth, newHeight;
                newWidth = Math.max(MIN_CANVAS_WIDTH, Math.min(availableWidth, 800));
                newHeight = newWidth / aspectRatio;

                if (newHeight > availableHeight) {
                    newHeight = availableHeight;
                    newWidth = newHeight * aspectRatio;
                }

                setCanvasSize({ width: newWidth, height: newHeight });
                setEditorHeight(window.innerHeight - 200);
                setEditorwidth(isDesktop ? window.innerWidth * 0.5 : window.innerWidth - 50);
            }
        };

        updateCanvasSize();
        window.addEventListener('resize', updateCanvasSize);
        return () => window.removeEventListener('resize', updateCanvasSize);
    }, [selectedWall, isDesktop, floorPlanData]);

    useEffect(() => {
        const newInputValues = {};
        Object.entries(walls).forEach(([wall, value]) => {
            newInputValues[`${wall}Width`] = formatValue(value, unit);
            const coords = floorPlanData?.wallCoordinates?.[wall];
            if (coords) {
                newInputValues[`${wall}Height`] = formatValue(coords.height, unit);
            }
        });
        setInputValues(newInputValues);
    }, [unit, walls]);

    useEffect(() => {
        if (initialAppliancesData) {
            setAppliances(initialAppliancesData);
        } else {
            console.log('no appliances');
        }
    }, [initialAppliancesData]);

    useEffect(() => {
        drawFloorPlan();
    }, [
        walls,
        kitchenItems,
        scale,
        canvasSize,
        selectedWall,
        selectedItems,
        currentSelectedIndex,
        isAddingBaseCabinet,
        wallElements,
    ]);

    useEffect(() => {
        if (initialKitchenLayout) {
            const applianceSet = new Set(
                initialKitchenLayout.filter(item => item.type === 'appliance').map(item => item.applianceType)
            );
            setAddedAppliances(applianceSet);
        }
    }, [initialKitchenLayout]);

    // Add click outside handler
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (applianceMenuRef.current && !applianceMenuRef.current.contains(event.target)) {
                setIsApplianceMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const toggleAppliance = appliance => {
        const applianceType = appliance.id;

        if (addedAppliances.has(applianceType)) {
            // Remove the appliance
            setKitchenItems(prevItems =>
                prevItems.filter(item => !(item.type === 'appliance' && item.applianceType === applianceType))
            );
            setAddedAppliances(prev => {
                const newSet = new Set(prev);
                newSet.delete(applianceType);
                return newSet;
            });
        } else {
            // Add the appliance
            addAppliance(appliance);
            setAddedAppliances(prev => {
                const newSet = new Set(prev);
                newSet.add(applianceType);
                return newSet;
            });
        }
    };

    // Add these utility functions at the top level
    const calculateMaxWidth = coordinates => {
        if (!coordinates) return 0;
        let maxX = -Infinity;
        let minX = Infinity;

        Object.values(coordinates).forEach(wall => {
            maxX = Math.max(maxX, wall.x1, wall.x2);
            minX = Math.min(minX, wall.x1, wall.x2);
        });

        return maxX - minX;
    };

    const calculateMaxDepth = coordinates => {
        if (!coordinates) return 0;
        let maxY = -Infinity;
        let minY = Infinity;

        Object.values(coordinates).forEach(wall => {
            maxY = Math.max(maxY, wall.y1, wall.y2);
            minY = Math.min(minY, wall.y1, wall.y2);
        });

        return maxY - minY;
    };

    const drawFloorPlan = async () => {
        const canvas = canvasRef.current;
        if (!canvas || !floorPlanData) return;

        const ctx = canvas.getContext('2d');

        // First calculate all the values we need
        const requiredWidth = calculateMaxWidth(wallCoordinates);
        const requiredHeight = calculateMaxDepth(wallCoordinates);

        const padding = 40;
        const scaleX = (canvas.width - padding * 2) / requiredWidth;
        const scaleY = (canvas.height - padding * 2) / requiredHeight;
        const newScale = Math.min(scaleX, scaleY);
        const newStartX = (canvas.width - requiredWidth * newScale) / 2;
        const newStartY = (canvas.height - requiredHeight * newScale) / 2;

        // Update state only if values have changed
        if (scale !== newScale) setScale(newScale);
        if (startX !== newStartX) setStartX(newStartX);
        if (startY !== newStartY) setStartY(newStartY);

        // Clear the canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Draw grid using the new values directly
        ctx.strokeStyle = '#E5E5E5';
        ctx.lineWidth = 0.5;
        const gridSize = 12 * newScale;

        // Draw vertical lines
        for (let x = newStartX % gridSize; x < canvas.width; x += gridSize) {
            ctx.beginPath();
            ctx.moveTo(x, 0);
            ctx.lineTo(x, canvas.height);
            ctx.stroke();
        }

        // Draw horizontal lines
        for (let y = newStartY % gridSize; y < canvas.height; y += gridSize) {
            ctx.beginPath();
            ctx.moveTo(0, y);
            ctx.lineTo(canvas.width, y);
            ctx.stroke();
        }

        // Reset line style for room drawing
        ctx.lineWidth = 1;
        ctx.strokeStyle = 'black';

        const { roomType, walls, wallElements, unit } = floorPlanData;
        // const coordinates = getWallCoordinates(walls, roomType);
        const coordinates = getWallCoordinates(walls, roomType, floorPlanData.wallCoordinates)
        console.log('coordinates:', coordinates);
        setWallCoordinates(coordinates);
        floorPlanData.wallCoordinates = coordinates;

        if (isAddingBaseCabinet) {
            ctx.save();
            Object.values(coordinates).forEach(coords => {
                ctx.strokeStyle = 'rgba(255, 255, 0, 0.5)'; // Yellow highlight
                ctx.lineWidth = 10;
                ctx.beginPath();
                ctx.moveTo(coords.x1 * scale + startX, coords.y1 * scale + startY);
                ctx.lineTo(coords.x2 * scale + startX, coords.y2 * scale + startY);
                ctx.stroke();
            });
            ctx.restore();
        }

        // Use the new values directly instead of state
        await drawRoom(ctx, walls, coordinates, wallElements, newStartX, newStartY, newScale, unit);

        if (selectedWall && coordinates[selectedWall] && !isAddingBaseCabinet) {
            highlightWall(ctx, coordinates[selectedWall], newStartX, newStartY, newScale);
        }

        await drawKitchenItem(ctx, kitchenItems, newStartX, newStartY, newScale, selectedItems, currentSelectedIndex);
    };

    const renderWallDimensionControls = () => {
        if (!selectedWall || !wallCoordinates[selectedWall] || !canvasRef.current) return null;

        // Control box fixed dimensions (before scaling)
        const CONTROL_WIDTH = 160;
        const CONTROL_HEIGHT = 30;
        const OFFSET_DISTANCE = 40;

        // Get actual rendered canvas dimensions
        const canvasRect = canvasRef.current.getBoundingClientRect();
        const scaleRatio = canvasRect.width / canvasSize.width;

        // Calculate actual sizes after scaling
        const controlBoxWidth = CONTROL_WIDTH * scaleRatio;
        const controlBoxHeight = CONTROL_HEIGHT * scaleRatio;
        const offsetDistance = OFFSET_DISTANCE * scaleRatio;

        const coords = wallCoordinates[selectedWall];
        const angle = (Math.atan2(coords.y2 - coords.y1, coords.x2 - coords.x1) * 180) / Math.PI;

        // Calculate midpoint with scaling
        const midX = ((coords.x1 + coords.x2) / 2) * scale + startX;
        const midY = ((coords.y1 + coords.y2) / 2) * scale + startY;

        // Calculate perpendicular angle for offset
        const perpAngle = angle + 90;
        let offsetX = offsetDistance * Math.cos((perpAngle * Math.PI) / 180);
        let offsetY = offsetDistance * Math.sin((perpAngle * Math.PI) / 180);

        // Initial position calculation
        let potentialX = midX * scaleRatio;
        let potentialY = midY * scaleRatio;

        // Strict boundaries considering control box size
        const minX = 0;
        const maxX = canvasRect.width - controlBoxWidth;
        const minY = 0;
        const maxY = canvasRect.height - controlBoxHeight;

        // Add offsets
        potentialX += offsetX;
        potentialY += offsetY;

        // First boundary check
        if (potentialX < minX) {
            potentialX = minX;
        } else if (potentialX > maxX) {
            // Try positioning on the other side of the wall
            potentialX = midX * scaleRatio - offsetX;
            // If still out of bounds, force to max
            if (potentialX > maxX) {
                potentialX = maxX;
            }
        }

        if (potentialY < minY) {
            potentialY = minY;
        } else if (potentialY > maxY) {
            // Try positioning on the other side of the wall
            potentialY = midY * scaleRatio - offsetY;
            // If still out of bounds, force to max
            if (potentialY > maxY) {
                potentialY = maxY;
            }
        }

        return (
            <div
                style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    transform: `translate(${potentialX}px, ${potentialY}px)`,
                    display: 'flex',
                    gap: '4px',
                    background: 'white',
                    padding: '4px',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    border: '1px solid #e2e8f0',
                    zIndex: 10,
                    fontSize: '12px',
                    maxWidth: `${CONTROL_WIDTH}px`,
                    height: `${CONTROL_HEIGHT}px`,
                }}
            >
                <div className="flex items-center gap-1">
                    <Label className="text-[10px]">W:</Label>
                    <Input
                        type="number"
                        value={inputValues[`${selectedWall}Width`] || ''}
                        onChange={e => handleWallChange(selectedWall, e.target.value, 'width')}
                        className="w-12 h-6 text-[12px] px-1"
                    />
                </div>
                <div className="flex items-center gap-1">
                    <Label className="text-[12px]">H:</Label>
                    <Input
                        type="number"
                        value={inputValues[`${selectedWall}Height`] || ''}
                        onChange={e => handleWallChange(selectedWall, e.target.value, 'height')}
                        className="w-12 h-6 text-[12px] px-1"
                    />
                </div>
                <span className="text-[12px] self-center">{unit}</span>
            </div>
        );
    };

    const handleWallElementsChange = newElements => {
        setWallElements(prev => {
            const updated = { ...prev, [selectedWall]: newElements };
            // Update floorPlanData with new wall elements
            if (floorPlanData) {
                floorPlanData.wallElements = updated;
            }
            return updated;
        });
    };

    const rotateSelectedItem = () => {
        if (selectedItems.length > 0) {
            const itemIndex = selectedItems[currentSelectedIndex];
            const item = kitchenItems[itemIndex];

            if (item.type === 'island' || item.type === 'appliance') {
                setKitchenItems(prevItems => {
                    const newItems = [...prevItems];
                    const centerX = (item.x1 + item.x3) / 2;
                    const centerY = (item.y1 + item.y3) / 2;

                    // Rotate by 45 degrees (π/4 radians)
                    const newAngle = item.angle + Math.PI / 4;

                    // Update direction based on new angle
                    const newDirX = Math.cos(newAngle);
                    const newDirY = Math.sin(newAngle);

                    // Calculate new corner positions
                    const halfWidth = item.width / 2;
                    const halfDepth = item.depth / 2;

                    newItems[itemIndex] = {
                        ...item,
                        x1: centerX - halfWidth * newDirX + halfDepth * newDirY,
                        y1: centerY - halfWidth * newDirY - halfDepth * newDirX,
                        x2: centerX + halfWidth * newDirX + halfDepth * newDirY,
                        y2: centerY + halfWidth * newDirY - halfDepth * newDirX,
                        x3: centerX + halfWidth * newDirX - halfDepth * newDirY,
                        y3: centerY + halfWidth * newDirY + halfDepth * newDirX,
                        x4: centerX - halfWidth * newDirX - halfDepth * newDirY,
                        y4: centerY - halfWidth * newDirY + halfDepth * newDirX,
                        angle: newAngle,
                        direction: { x: newDirX, y: newDirY },
                    };

                    return newItems;
                });
            }
        }
    };

    const findClickedItems = (x, y) => {
        return kitchenItems.reduce((acc, item, index) => {
            const minX = Math.min(item.x1, item.x2, item.x3, item.x4);
            const maxX = Math.max(item.x1, item.x2, item.x3, item.x4);
            const minY = Math.min(item.y1, item.y2, item.y3, item.y4);
            const maxY = Math.max(item.y1, item.y2, item.y3, item.y4);

            if (
                x >= minX * scale + startX &&
                x <= maxX * scale + startX &&
                y >= minY * scale + startY &&
                y <= maxY * scale + startY
            ) {
                acc.push(index);
            }
            return acc;
        }, []);
    };

    const cycleSelectedElement = () => {
        if (selectedItems.length > 1) {
            setCurrentSelectedIndex(prevIndex => (prevIndex + 1) % selectedItems.length);
        }
    };

    const deleteSelectedElement = () => {
        if (selectedItems.length > 0) {
            const indexToDelete = selectedItems[currentSelectedIndex];
            setKitchenItems(prev => prev.filter((_, index) => index !== indexToDelete));
            setSelectedItems(prev =>
                prev.filter(index => index !== indexToDelete).map(index => (index > indexToDelete ? index - 1 : index))
            );
            if (selectedItems.length === 1) {
                setSelectedItems([]);
            } else {
                setCurrentSelectedIndex(0);
            }
        }
    };

    const findClickedWall = (x, y) => {
        for (const [wall, coords] of Object.entries(wallCoordinates)) {
            if (
                isPointNearLine(
                    x,
                    y,
                    coords.x1 * scale + startX,
                    coords.y1 * scale + startY,
                    coords.x2 * scale + startX,
                    coords.y2 * scale + startY
                )
            ) {
                return wall;
            }
        }
        return null;
    };

    const handleCanvasClick = event => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const clientX = event.touches ? event.touches[0].clientX : event.clientX;
        const clientY = event.touches ? event.touches[0].clientY : event.clientY;
        const x = (clientX - rect.left) * (canvas.width / rect.width);
        const y = (clientY - rect.top) * (canvas.height / rect.height);

        if (isAddingBaseCabinet) {
            // Check if click is near any wall with a wider threshold (30 pixels)
            const isNearAnyWall = Object.values(wallCoordinates).some(coords =>
                isPointNearLine(
                    x,
                    y,
                    coords.x1 * scale + startX,
                    coords.y1 * scale + startY,
                    coords.x2 * scale + startX,
                    coords.y2 * scale + startY,
                    30
                )
            );

            const clickedWall = findClickedWall(x, y);
            if (clickedWall) {
                addBaseCabinets(clickedWall);
            } else if (!isNearAnyWall) {
                setIsAddingBaseCabinet(false);
            }
            //ui.shadcn.com/docs/components/dialog
            https: return;
        }

        const clickedItems = findClickedItems(x, y);
        if (clickedItems.length > 0) {
            setSelectedItems(clickedItems);
            setCurrentSelectedIndex(0);
            setSelectedWall(null);
        } else {
            const clickedWall = findClickedWall(x, y);
            if (clickedWall) {
                setSelectedWall(clickedWall);
                setSelectedItems([]);
            } else {
                setSelectedWall(null);
                setSelectedItems([]);
            }
        }
    };

    const handleCanvasMouseDown = event => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const clientX = event.touches ? event.touches[0].clientX : event.clientX;
        const clientY = event.touches ? event.touches[0].clientY : event.clientY;
        const x = (clientX - rect.left) * (canvas.width / rect.width);
        const y = (clientY - rect.top) * (canvas.height / rect.height);
        setDragStart({ x, y });

        if (selectedItems.length > 0) {
            const item = kitchenItems[selectedItems[currentSelectedIndex]];
            const handleSize = 15 / scale;
            // console.log("item:",item)
            if (item.type === 'appliance') {
                if (isPointInsideItem(x, y, item)) {
                    setisDraggingItem(true);
                    return;
                }
            }

            if (item.type === 'island') {
                // Check all handles and set appropriate handle type
                const leftMid = Math.hypot(
                    x - (((item.x1 + item.x4) * scale) / 2 + startX),
                    y - (((item.y1 + item.y4) * scale) / 2 + startY)
                );
                const rightMid = Math.hypot(
                    x - (((item.x2 + item.x3) * scale) / 2 + startX),
                    y - (((item.y2 + item.y3) * scale) / 2 + startY)
                );
                const topMid = Math.hypot(
                    x - (((item.x1 + item.x2) * scale) / 2 + startX),
                    y - (((item.y1 + item.y2) * scale) / 2 + startY)
                );
                const bottomMid = Math.hypot(
                    x - (((item.x4 + item.x3) * scale) / 2 + startX),
                    y - (((item.y4 + item.y3) * scale) / 2 + startY)
                );

                if (leftMid < handleSize) {
                    setDraggedHandle('start');
                } else if (rightMid < handleSize) {
                    setDraggedHandle('end');
                } else if (topMid < handleSize) {
                    setDraggedHandle('top');
                } else if (bottomMid < handleSize) {
                    setDraggedHandle('bottom');
                } else if (isPointInsideItem(x, y, item)) {
                    setisDraggingItem(true);
                }
            } else {
                // Handle other item types (base cabinets, etc.)
                if (
                    Math.hypot(
                        x - (((item.x1 + item.x4) * scale) / 2 + startX),
                        y - (((item.y1 + item.y4) * scale) / 2 + startY)
                    ) < handleSize
                ) {
                    setDraggedHandle('start');
                } else if (
                    Math.hypot(
                        x - (((item.x2 + item.x3) * scale) / 2 + startX),
                        y - (((item.y2 + item.y3) * scale) / 2 + startY)
                    ) < handleSize
                ) {
                    setDraggedHandle('end');
                }
            }
        }
    };

    const getHandleDirection = (item, x, y) => {
        if (!item) return null;
        const handleSize = 15 / scale;

        // Convert to scaled coordinates
        const handlePositions = {
            left: {
                x: ((item.x1 + item.x4) * scale) / 2 + startX,
                y: ((item.y1 + item.y4) * scale) / 2 + startY,
            },
            right: {
                x: ((item.x2 + item.x3) * scale) / 2 + startX,
                y: ((item.y2 + item.y3) * scale) / 2 + startY,
            },
        };

        // Add top and bottom handles for islands
        if (item.type === 'island') {
            handlePositions.top = {
                x: ((item.x1 + item.x2) * scale) / 2 + startX,
                y: ((item.y1 + item.y2) * scale) / 2 + startY,
            };
            handlePositions.bottom = {
                x: ((item.x4 + item.x3) * scale) / 2 + startX,
                y: ((item.y4 + item.y3) * scale) / 2 + startY,
            };
        }

        // Check each handle position
        for (const [position, coords] of Object.entries(handlePositions)) {
            const distance = Math.hypot(x - coords.x, y - coords.y);
            if (distance < handleSize) {
                switch (position) {
                    case 'left':
                    case 'right':
                        return item.type === 'island'
                            ? 'ew-resize'
                            : `${Math.abs(Math.cos(item.angle)) > 0.5 ? 'ew' : 'ns'}-resize`;
                    case 'top':
                    case 'bottom':
                        return `${Math.abs(Math.sin(item.angle)) > 0.5 ? 'ns' : 'ns'}-resize`;
                    default:
                        return 'pointer';
                }
            }
        }

        return null;
    };

    const handleCanvasMouseMove = async event => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const rect = canvas.getBoundingClientRect();
        const clientX = event.touches ? event.touches[0].clientX : event.clientX;
        const clientY = event.touches ? event.touches[0].clientY : event.clientY;
        const x = (clientX - rect.left) * (canvas.width / rect.width);
        const y = (clientY - rect.top) * (canvas.height / rect.height);

        // If there's a selected item, check for handles first
        if (selectedItems.length > 0) {
            const selectedItem = kitchenItems[selectedItems[currentSelectedIndex]];
            const handleCursor = getHandleDirection(selectedItem, x, y);
            if (handleCursor) {
                canvas.style.cursor = handleCursor;
                return;
            }
        }

        // Check if over any kitchen items
        const isOverItem = kitchenItems.some(item => {
            const minX = Math.min(item.x1, item.x2, item.x3, item.x4);
            const maxX = Math.max(item.x1, item.x2, item.x3, item.x4);
            const minY = Math.min(item.y1, item.y2, item.y3, item.y4);
            const maxY = Math.max(item.y1, item.y2, item.y3, item.y4);

            return (
                x >= minX * scale + startX &&
                x <= maxX * scale + startX &&
                y >= minY * scale + startY &&
                y <= maxY * scale + startY
            );
        });

        // If not over a kitchen item, check if over a wall
        const isOverWall = !isOverItem && findClickedWall(x, y);

        // Set cursor based on what we're hovering over
        canvas.style.cursor = isOverItem || isOverWall ? 'pointer' : 'default';

        // Handle dragging if active
        if (isDraggingItem || draggedHandle) {
            await handleItemDrag(x, y);
        }
    };

    const handleItemDrag = async (x, y) => {
        if (selectedItems.length === 0) return;

        setKitchenItems(prevItems => {
            const newItems = [...prevItems];
            const elementIndex = selectedItems[currentSelectedIndex];
            const item = newItems[elementIndex];

            if (isDraggingItem) {
                const dx = (x - dragStart.x) / scale;
                const dy = (y - dragStart.y) / scale;
                item.x1 += dx;
                item.y1 += dy;
                item.x2 += dx;
                item.y2 += dy;
                item.x3 += dx;
                item.y3 += dy;
                item.x4 += dx;
                item.y4 += dy;
                setDragStart({ x, y });
            } else {
                if (item.type === 'island') {
                    handleIslandResize(item, x, y);
                } else {
                    handleBaseCabinetResize(item, x, y);
                }
            }
            return newItems;
        });

        drawFloorPlan();
    };

    const handleClickTouchEnd = () => {
        setisDraggingItem(false);
        setDraggedHandle(null);
    };

    const isPointInsideItem = (x, y, item) => {
        const minX = Math.min(item.x1, item.x2, item.x3, item.x4);
        const maxX = Math.max(item.x1, item.x2, item.x3, item.x4);
        const minY = Math.min(item.y1, item.y2, item.y3, item.y4);
        const maxY = Math.max(item.y1, item.y2, item.y3, item.y4);
        return (
            x >= minX * scale + startX &&
            x <= maxX * scale + startX &&
            y >= minY * scale + startY &&
            y <= maxY * scale + startY
        );
    };

    const isPointNearLine = (px, py, x1, y1, x2, y2, threshold = 10) => {
        const A = px - x1;
        const B = py - y1;
        const C = x2 - x1;
        const D = y2 - y1;

        const dot = A * C + B * D;
        const len_sq = C * C + D * D;
        const param = len_sq !== 0 ? dot / len_sq : -1;

        let xx, yy;

        if (param < 0) {
            xx = x1;
            yy = y1;
        } else if (param > 1) {
            xx = x2;
            yy = y2;
        } else {
            xx = x1 + param * C;
            yy = y1 + param * D;
        }

        const dx = px - xx;
        const dy = py - yy;

        return Math.sqrt(dx * dx + dy * dy) <= threshold;
    };

    const projectPointOnLine = (px, py, x1, y1, x2, y2) => {
        const dx = x2 - x1;
        const dy = y2 - y1;
        const t = ((px - x1) * dx + (py - y1) * dy) / (dx * dx + dy * dy);
        return {
            x: x1 + t * dx,
            y: y1 + t * dy,
        };
    };

    const handleIslandResize = (item, px, py) => {
        const x = (px - startX) / scale;
        const y = (py - startY) / scale;

        switch (draggedHandle) {
            case 'start':
                const newStart = projectPointOnLine(x, y, item.x1, item.y1, item.x2, item.y2);
                item.x1 = newStart.x;
                item.y1 = newStart.y;
                item.x4 = newStart.x - item.direction.y * item.depth;
                item.y4 = newStart.y + item.direction.x * item.depth;
                break;
            case 'end':
                const newEnd = projectPointOnLine(x, y, item.x1, item.y1, item.x2, item.y2);
                item.x2 = newEnd.x;
                item.y2 = newEnd.y;
                item.x3 = newEnd.x - item.direction.y * item.depth;
                item.y3 = newEnd.y + item.direction.x * item.depth;
                break;
            case 'top':
                const newTop = projectPointOnLine(x, y, item.x4, item.y4, item.x1, item.y1);
                item.x1 = newTop.x;
                item.y1 = newTop.y;
                item.x2 = newTop.x + item.direction.x * item.width;
                item.y2 = newTop.y + item.direction.y * item.width;
                break;
            case 'bottom':
                const newBottom = projectPointOnLine(x, y, item.x4, item.y4, item.x1, item.y1);
                item.x3 = newBottom.x + item.direction.x * item.width;
                item.y3 = newBottom.y + item.direction.y * item.width;
                item.x4 = newBottom.x;
                item.y4 = newBottom.y;
                break;
            default:
                break;
        }
        item.width = Math.hypot(item.x2 - item.x1, item.y2 - item.y1);
        item.depth = Math.hypot(item.x3 - item.x2, item.y3 - item.y2);
    };

    const handleBaseCabinetResize = (item, px, py) => {
        const x = (px - startX) / scale;
        const y = (py - startY) / scale;
        if (draggedHandle === 'start') {
            const newStart = projectPointOnLine(x, y, item.x1, item.y1, item.x2, item.y2);
            item.x1 = newStart.x;
            item.y1 = newStart.y;
            item.x4 = newStart.x - item.direction.y * item.depth;
            item.y4 = newStart.y + item.direction.x * item.depth;
        } else if (draggedHandle === 'end') {
            const newEnd = projectPointOnLine(x, y, item.x1, item.y1, item.x2, item.y2);
            item.x2 = newEnd.x;
            item.y2 = newEnd.y;
            item.x3 = newEnd.x - item.direction.y * item.depth;
            item.y3 = newEnd.y + item.direction.x * item.depth;
        }
        item.width = Math.hypot(item.x2 - item.x1, item.y2 - item.y1);
    };

    const addBaseCabinets = wall => {
        if (!wall || !wallCoordinates[wall]) return;

        const { x1, y1, x2, y2 } = wallCoordinates[wall];
        const wallElements = floorPlanData.wallElements[wall] || [];
        const doors = wallElements.filter(element => element.type === 'door');
        const openings = wallElements.filter(element => element.type === 'opening');

        // ... (keep existing cabinet creation code)
        let cabinetSections = [];

        // Calculate wall direction and length
        const dx = x2 - x1;
        const dy = y2 - y1;
        const wallLength = Math.sqrt(dx * dx + dy * dy);
        const wallAngle = Math.atan2(dy, dx);

        // Normalize direction vector
        const dirX = dx / wallLength;
        const dirY = dy / wallLength;

        // Perpendicular direction for cabinet depth
        const perpX = -dirY;
        const perpY = dirX;

        const doorAreas = [
            ...doors.map(door => ({
                start: door.x,
                end: door.x + door.width,
                type: 'door',
            })),
            ...openings.map(opening => ({
                start: opening.x,
                end: opening.x + opening.width,
                type: 'opening',
            })),
        ];

        cabinetSections = getCabinetSections(0, wallLength, wallLength, doorAreas, true);

        cabinetSections.forEach(section => {
            const sectionStartX = x1 + dirX * section.start;
            const sectionStartY = y1 + dirY * section.start;
            const sectionEndX = x1 + dirX * section.end;
            const sectionEndY = y1 + dirY * section.end;

            const newCabinet = {
                type: 'baseCabinets',
                wall: wall,
                x1: sectionStartX,
                y1: sectionStartY,
                z1: 0,
                x2: sectionEndX,
                y2: sectionEndY,
                x3: sectionEndX + perpX * BASE_CABINET_DEPTH,
                y3: sectionEndY + perpY * BASE_CABINET_DEPTH,
                x4: sectionStartX + perpX * BASE_CABINET_DEPTH,
                y4: sectionStartY + perpY * BASE_CABINET_DEPTH,
                depth: BASE_CABINET_DEPTH,
                width: section.end - section.start,
                height: BASE_CABINET_HEIGHT,
                direction: { x: dirX, y: dirY },
                angle: wallAngle,
            };
            setKitchenItems(prev => [...prev, newCabinet]);
        });
    };

    const addIsland = () => {
        const centerX = (canvasSize.width / 2 - startX) / scale;
        const centerY = (canvasSize.height / 2 - startY) / scale;
        const islandWidth = 60; // Default width in inches
        const islandDepth = 30; // Default depth in inches
        // const emptySpace = findEmptySpace(floorPlanData, kitchenItems);
        // const innerSpace = findInnerSpace(floorPlanData, kitchenItems);

        const x1 = centerX - islandWidth / 2;
        const y1 = centerY - islandDepth / 2;
        const x2 = centerX + islandWidth / 2;
        const y2 = centerY - islandDepth / 2;

        // Calculate direction and length
        const dx = x2 - x1;
        const dy = y2 - y1;
        const Length = Math.sqrt(dx * dx + dy * dy);
        const Angle = Math.atan2(dy, dx);

        // Normalize direction vector
        const dirX = dx / Length;
        const dirY = dy / Length;

        const newIsland = {
            type: 'island',
            wall: 'None',
            x1: x1,
            y1: y1,
            z1: 0,
            x2: x2,
            y2: y2,
            x3: centerX + islandWidth / 2,
            y3: centerY + islandDepth / 2,
            x4: centerX - islandWidth / 2,
            y4: centerY + islandDepth / 2,
            width: islandWidth,
            depth: islandDepth,
            height: BASE_CABINET_HEIGHT,
            direction: { x: dirX, y: dirY },
            angle: Angle,
        };

        setKitchenItems(prev => [...prev, newIsland]);
        setIsIslandDialogOpen(false);
    };

    const addAppliance = appliance => {
        const centerX = (canvasSize.width / 2 - startX) / scale;
        const centerY = (canvasSize.height / 2 - startY) / scale;

        // Calculate initial position and angle
        const width = appliance.dimensions.width;
        const depth = appliance.dimensions.depth;
        const x1 = centerX - width / 2;
        const y1 = centerY - depth / 2;
        const x2 = centerX + width / 2;
        const y2 = centerY - depth / 2;

        // Calculate direction and length
        const dx = x2 - x1;
        const dy = y2 - y1;
        const length = Math.sqrt(dx * dx + dy * dy);
        const angle = Math.atan2(dy, dx);

        // Normalize direction vector
        const dirX = dx / length;
        const dirY = dy / length;

        const newAppliance = {
            type: 'appliance',
            applianceType: appliance.id,
            name: appliance.name,
            x1: x1,
            y1: y1,
            z1: 0,
            x2: x2,
            y2: y2,
            x3: centerX + width / 2,
            y3: centerY + depth / 2,
            x4: centerX - width / 2,
            y4: centerY + depth / 2,
            width: width,
            depth: depth,
            height: appliance.dimensions.height,
            direction: { x: dirX, y: dirY },
            angle: angle,
            dimensions: appliance.dimensions,
        };

        setKitchenItems(prev => [...prev, newAppliance]);
    };

    const getCabinetSections = (wallStart, wallEnd, wallLength, doorAreas, direction) => {
        let sections = [];
        let currentPos = wallStart;

        if (direction) {
            doorAreas.sort((a, b) => a.start - b.start);
        } else {
            doorAreas.sort((a, b) => b.start - a.start);
        }

        doorAreas.forEach(door => {
            if (direction ? door.start > currentPos : door.start < currentPos) {
                sections.push({
                    start: currentPos,
                    end: door.start,
                });
            }
            currentPos = door.end;
        });

        if (direction ? currentPos < wallEnd : currentPos > wallEnd) {
            sections.push({
                start: currentPos,
                end: wallEnd,
            });
        }

        return sections;
    };

    // Group appliances by category
    const groupedAppliances = appliances.reduce((acc, appliance) => {
        const category = appliance.category || 'Other';
        if (!acc[category]) {
            acc[category] = [];
        }
        acc[category].push(appliance);
        return acc;
    }, {});

    const handleWallChange = (wall, value, dimension = 'width') => {
        const inputKey = dimension === 'width' ? `${wall}Width` : `${wall}Height`;
        setInputValues(prev => ({ ...prev, [inputKey]: value }));
        const numValue = parseFloat(value);

        if (!isNaN(numValue)) {
            const inchValue = convertToInches(numValue, unit);

            if (dimension === 'width') {
                const MIN_WALL_LENGTH = 10;
                if (inchValue < MIN_WALL_LENGTH) {
                    toast({
                        title: 'Invalid wall length',
                        description: `Wall length must be at least ${formatValue(MIN_WALL_LENGTH, unit)} ${unit}.`,
                        variant: 'destructive',
                    });
                    return;
                }

                setWalls(prevWalls => {
                    const newWalls = { ...prevWalls, [wall]: inchValue };
                    const roomType = floorPlanData.roomType;

                    // Logic for rectangular room
                    if (roomType === 'rectangular') {
                        if (wall === 'back' || wall === 'front') {
                            newWalls.back = newWalls.front = inchValue;
                        } else if (wall === 'left' || wall === 'right') {
                            newWalls.left = newWalls.right = inchValue;
                        }
                    }

                    // Logic for L-shaped room
                    else if (roomType === 'l-shaped') {
                        if (wall === 'back') {
                            // Ensure lFrontR + lFrontL = back
                            const difference = inchValue - (newWalls.lFrontR + newWalls.lFrontL);
                            newWalls.lFrontR += difference / 2;
                            newWalls.lFrontL += difference / 2;
                        } else if (wall === 'lFrontR' || wall === 'lFrontL') {
                            // Adjust the other front wall to maintain total back wall length
                            const otherWall = wall === 'lFrontR' ? 'lFrontL' : 'lFrontR';
                            newWalls[otherWall] = newWalls.back - inchValue;
                        } else if (wall === 'left') {
                            // Ensure left = lRight + lShortSide
                            const difference = inchValue - (newWalls.lRight + newWalls.lShortSide);
                            newWalls.lRight += difference / 2;
                            newWalls.lShortSide += difference / 2;
                        } else if (wall === 'lRight' || wall === 'lShortSide') {
                            // Adjust the other side wall to maintain total left wall length
                            const otherWall = wall === 'lRight' ? 'lShortSide' : 'lRight';
                            newWalls[otherWall] = newWalls.left - inchValue;
                        }
                    }

                    // Logic for U-shaped room
                    else if (roomType === 'u-shaped') {
                        if (wall === 'back') {
                            newWalls.back = inchValue;
                        } else if (wall === 'left') {
                            newWalls.left = inchValue;
                        } else if (wall === 'right') {
                            newWalls.right = inchValue;
                        }
                    }

                    // Logic for right open rectangular room
                    else if (roomType === 'rightOpenRectangular') {
                        if (wall === 'back') {
                            newWalls.back = inchValue;
                            if (newWalls.openFront > inchValue) {
                                newWalls.openFront = inchValue;
                            }
                        } else if (wall === 'openFront') {
                            if (inchValue > newWalls.back) {
                                newWalls.openFront = newWalls.back;
                            } else {
                                newWalls.openFront = inchValue;
                            }
                        } else if (wall === 'left' || wall === 'right') {
                            newWalls.left = newWalls.right = inchValue;
                        }
                    }

                    // Logic for left open rectangular room
                    else if (roomType === 'leftOpenRectangular') {
                        if (wall === 'back') {
                            newWalls.back = inchValue;
                            if (newWalls.openFront > inchValue) {
                                newWalls.openFront = inchValue;
                            }
                        } else if (wall === 'openFront') {
                            if (inchValue > newWalls.back) {
                                newWalls.openFront = newWalls.back;
                            } else {
                                newWalls.openFront = inchValue;
                            }
                        } else if (wall === 'left' || wall === 'right') {
                            newWalls.left = newWalls.right = inchValue;
                        }
                    }

                    if (floorPlanData) {
                        floorPlanData.walls = newWalls;
                    }

                    return newWalls;
                });
            } else {
                const MIN_WALL_HEIGHT = 24;
                if (inchValue < MIN_WALL_HEIGHT) {
                    toast({
                        title: 'Invalid wall height',
                        description: `Wall height must be at least ${formatValue(MIN_WALL_HEIGHT, unit)} ${unit}.`,
                        variant: 'destructive',
                    });
                    return;
                }

                if (floorPlanData?.wallCoordinates?.[wall]) {
                    const newCoordinates = {
                        ...floorPlanData.wallCoordinates,
                        [wall]: {
                            ...floorPlanData.wallCoordinates[wall],
                            height: inchValue
                        }
                    };
                    floorPlanData.wallCoordinates = newCoordinates;
                    console.log("wall height:", floorPlanData.wallCoordinates[wall].height ,'florplandata:', floorPlanData);
                    setWallCoordinates(newCoordinates);

                    return newCoordinates;
                }
            }
        }
    };

    useEffect(() => {
        onSave({ kitchenItems, floorPlanData });
    }, [kitchenItems, floorPlanData]);

    return (
        // <div className="w-full flex flex-col items-center" ref={containerRef}>
        <div className="w-full h-full">
            {/* Fixed position tip panel */}
            {showTip && (
                <div className="fixed left-10 top-1/3 z-50 ">
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-400 shadow-lg w-48 relative">
                        <button
                            onClick={() => setShowTip(false)}
                            className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border border-blue-200 flex items-center justify-center text-blue-500 hover:bg-blue-50"
                        >
                            ×
                        </button>
                        <h4 className="font-bold text-red-800 mb-2">Tips:</h4>
                        <p className="text-sm text-blue-700">Click on any wall to:</p>
                        <ul className="text-sm text-blue-600 list-disc pl-4 mt-1 space-y-1">
                            <li>See wall elevation</li>
                            <li>Edit wall dimensions</li>
                            <li>Add windows & doors</li>
                            <li>Add utility points</li>
                        </ul>
                    </div>
                </div>
            )}

            {/* Main content area */}
            <div className="w-full flex flex-col">
                {/* Toolbar - always on top */}
                <div className="w-full mb-2">
                    <div className="flex flex-row gap-2 p-2">
                        <TooltipProvider>
                            {/* Base Cabinet Button */}
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="default"
                                        size="icon"
                                        onClick={() => setIsAddingBaseCabinet(!isAddingBaseCabinet)}
                                        className={`w-14 h-14 shrink-0 rounded-lg bg-white shadow-lg hover:shadow-xl transition-all duration-200 ease-in-out transform hover:scale-105 flex flex-col items-center justify-center gap-1 p-1
                                        ${isAddingBaseCabinet
                                            ? 'border-2 border-yellow-500 bg-yellow-200 text-yellow-600'
                                            : 'border-2 border-yellow-500 hover:bg-yellow-50 text-yellow-600'
                                        }`}
                                    >
                                        <Plus className="h-6 w-6" />
                                        <span className="text-[10px] font-medium leading-none">Cabinets</span>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>
                                        {isAddingBaseCabinet
                                            ? 'Click to stop adding cabinets'
                                            : 'Click to start adding base cabinets'}
                                    </p>
                                </TooltipContent>
                            </Tooltip>

                            {/* Island Button */}
                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <Button
                                        variant="default"
                                        size="icon"
                                        onClick={() => setIsIslandDialogOpen(true)}
                                        className="w-14 h-14 shrink-0 rounded-lg bg-white border-2 border-blue-500 hover:bg-blue-50 text-blue-600 shadow-lg hover:shadow-xl transition-all duration-200 ease-in-out transform hover:scale-105 flex flex-col items-center justify-center gap-1 p-1"
                                    >
                                        <Plus className="h-6 w-6" />
                                        <span className="text-[10px] font-medium leading-none">Island</span>
                                    </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p>Click to add a kitchen island</p>
                                </TooltipContent>
                            </Tooltip>

                            {/* Appliances Dropdown */}
                            <div className="relative" ref={applianceMenuRef}>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <Button
                                            variant="default"
                                            size="icon"
                                            onClick={() => setIsApplianceMenuOpen(!isApplianceMenuOpen)}
                                            className="w-14 h-14 shrink-0 rounded-lg bg-white border-2 border-green-500 hover:bg-green-50 text-green-600 shadow-lg hover:shadow-xl transition-all duration-200 ease-in-out transform hover:scale-105 flex flex-col items-center justify-center gap-1 p-1"
                                        >
                                            <Plus className="h-6 w-6" />
                                            <span className="text-[10px] font-medium leading-none">Appliances</span>
                                        </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>Click to view appliances</p>
                                    </TooltipContent>
                                </Tooltip>

                                {isApplianceMenuOpen && (
                                    <Card className="absolute z-50 mt-2 w-64 max-h-[60vh] overflow-y-auto">
                                        <ScrollArea className="p-4">
                                            {Object.entries(groupedAppliances).map(([category, categoryAppliances]) => (
                                                <div key={category} className="mb-4">
                                                    <h3 className="font-semibold mb-2 text-sm text-gray-700">{category}</h3>
                                                    <div className="grid grid-cols-2 gap-2">
                                                        {categoryAppliances.map((appliance, index) => {
                                                            const Icon = applianceIcons[appliance.id.split('-')[0]];
                                                            const isAdded = addedAppliances.has(appliance.id);

                                                            return (
                                                                <Button
                                                                    key={`${appliance.id}-${index}`}
                                                                    variant="default"
                                                                    onClick={() => toggleAppliance(appliance)}
                                                                    className={`p-2 h-auto flex flex-col items-center justify-center gap-1
                                                                        ${isAdded
                                                                            ? 'border-2 border-green-500 bg-lime-400 text-green-600'
                                                                            : 'bg-white border-2 border-green-500 hover:bg-green-50 text-green-600'
                                                                        }`}
                                                                >
                                                                    {Icon ? <Icon className="w-6 h-6" /> : appliance.name.slice(0, 3)}
                                                                    <span className="text-[10px] font-medium leading-none">
                                                                        {appliance.name}
                                                                    </span>
                                                                    <span className="text-[8px] text-gray-500">
                                                                        {`${appliance.dimensions.width}"×${appliance.dimensions.depth}"`}
                                                                    </span>
                                                                </Button>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="mt-4 pt-4 border-t">
                                                <Button 
                                                    onClick={() => {
                                                        setIsApplianceMenuOpen(false);
                                                        setIsApplianceSelectorOpen(true);
                                                    }}
                                                    className="w-full flex items-center justify-between"
                                                    variant="outline"
                                                >
                                                    <span>Manage Appliances</span>
                                                    <Settings className="h-4 w-4 ml-2" />
                                                </Button>
                                            </div>
                                        </ScrollArea>
                                    </Card>
                                )}
                            </div>
                        </TooltipProvider>
                    </div>
                </div>

                {/* Main content with responsive layout */}
                <div className={`w-full flex ${isDesktop ? 'flex-row' : 'flex-col'} gap-4`}>
                    {/* Canvas area */}
                    <div
                        className={`${selectedWall && isDesktop ? 'w-1/2' : 'flex-col items-center'}`}
                        ref={containerRef}
                    >
                        {/* <ScrollArea className="h-[calc(100vh-260px)]">                 */}
                        <ScrollArea>
                            <Card className="relative">
                                <canvas
                                    ref={canvasRef}
                                    width={canvasSize.width}
                                    height={canvasSize.height}
                                    className="border"
                                    onClick={handleCanvasClick}
                                    onMouseDown={handleCanvasMouseDown}
                                    onMouseMove={handleCanvasMouseMove}
                                    onMouseUp={handleClickTouchEnd}
                                    onMouseLeave={handleClickTouchEnd}
                                    onTouchStart={handleCanvasMouseDown}
                                    onTouchMove={handleCanvasMouseMove}
                                    onTouchEnd={handleClickTouchEnd}
                                    style={{
                                        width: '100%',
                                        height: 'auto',
                                        maxWidth: `${canvasSize.width}px`,
                                        maxHeight: `${canvasSize.height}px`,
                                        touchAction: 'none',
                                    }}
                                />
                                {renderWallDimensionControls()}
                                {selectedItems.length > 0 && (
                                    <div className="absolute top-2 right-2 flex flex-col gap-2">
                                        <Button
                                            onClick={deleteSelectedElement}
                                            className="bg-red-500 hover:bg-red-600 text-white p-2 rounded"
                                        >
                                            <Trash2 size={20} />
                                        </Button>
                                        {selectedItems.length > 1 && (
                                            <Button
                                                onClick={cycleSelectedElement}
                                                className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded"
                                            >
                                                <ChevronRight size={20} />
                                            </Button>
                                        )}
                                        {(kitchenItems[selectedItems[currentSelectedIndex]]?.type === 'island' ||
                                            kitchenItems[selectedItems[currentSelectedIndex]]?.type ===
                                                'appliance') && (
                                            <Button
                                                onClick={rotateSelectedItem}
                                                className="bg-green-500 hover:bg-green-600 text-white p-2 rounded"
                                            >
                                                <RotateCw size={20} />
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </Card>
                        </ScrollArea>
                    </div>

                    {/* Wall Element Editor */}
                    {selectedWall && (
                        <div
                            className={`${isDesktop ? 'w-1/2' : 'w-full'} border rounded-lg shadow-sm bg-white`}
                            style={{ maxHeight: `${editorHeight}px` }}
                        >
                            <WallElementEditor
                                wallName={selectedWall}
                                wallWidth={walls[selectedWall]}
                                wallHeight={wallCoordinates[selectedWall].height}
                                unit={unit}
                                elements={wallElements[selectedWall] || []}
                                onElementsChange={handleWallElementsChange}
                                onWallChange={handleWallChange}
                                // onClose={() => setSelectedWall(null)}
                                inputValues={inputValues}
                                setInputValues={setInputValues}
                                className="flex flex-col h-full"
                                containerHeight={editorHeight}
                                containerWidth={editorWidth}
                            />
                        </div>
                    )}
                </div>
            </div>

            <Dialog open={isIslandDialogOpen} onOpenChange={setIsIslandDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Add</DialogTitle>
                    </DialogHeader>
                    <Button onClick={addIsland}>Add Island</Button>
                </DialogContent>
            </Dialog>

            {/* Add the Appliance Selector Dialog */}
            <Dialog 
                open={isApplianceSelectorOpen} 
                onOpenChange={setIsApplianceSelectorOpen}
                className="w-full max-w-6xl"
            >
                <DialogContent className="max-w-6xl w-[90vw] h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>Manage Appliances</DialogTitle>
                    </DialogHeader>
                    <ApplianceSelector 
                        flowId={null}
                        projectId={projectId}
                        onComplete={async () => {
                            await onAppliancesUpdate();
                            setIsApplianceSelectorOpen(false);
                        }}
                    />
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default KitchenLayoutEditor;
